import { useEffect, useState } from "react";
import { api } from "./api";
import {
  Card,
  Empty,
  ErrorNotice,
  formatDate,
  formatDuration,
  Spinner,
} from "./components";

interface StatsData {
  summary: {
    completionPercentage: number; prescribedSets: number; loggedSets: number; unfinishedCount: number; closedIncompleteCount: number;
    workoutCount: number;
    totalSeconds: number;
    averageSeconds: number;
    lastWorkoutAt: string | null;
  };
  weekly: Array<{ week: string; workouts: number; seconds: number }>;
  improvements: Array<{
    id: string;
    name: string;
    firstWeight: number;
    lastWeight: number;
    improvement: number;
  }>;
}
export function Stats() {
  const [data, setData] = useState<StatsData | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api<StatsData>("/stats")
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);
  if (!data && !error) return <Spinner label="Calculating your stats" />;
  return (
    <>
      <header className="page-heading">
        <p className="eyebrow">Your progress only</p>
        <h1>Stats & improvements</h1>
        <p>
          Calculated from your complete workout history. Craig and Cazzy are
          never combined.
        </p>
      </header>
      {error && <ErrorNotice message={error} />}{" "}
      {data && (
        <>
          <div className="stat-grid">
            <Card><span>Sets completed</span><strong>{data.summary.completionPercentage ?? 0}%</strong><p>{data.summary.loggedSets ?? 0} / {data.summary.prescribedSets ?? 0} prescribed sets</p></Card>
            <Card><span>Unfinished</span><strong>{data.summary.unfinishedCount ?? 0}</strong><p>{data.summary.closedIncompleteCount ?? 0} closed incomplete</p></Card>
            <Card>
              <span>Workouts</span>
              <strong>{data.summary.workoutCount}</strong>
            </Card>
            <Card>
              <span>Total training</span>
              <strong>{formatDuration(data.summary.totalSeconds)}</strong>
            </Card>
            <Card>
              <span>Average</span>
              <strong>{formatDuration(data.summary.averageSeconds)}</strong>
            </Card>
            <Card>
              <span>Most recent</span>
              <strong>{formatDate(data.summary.lastWorkoutAt)}</strong>
            </Card>
          </div>
          <Card>
            <h2>Last 12 weeks</h2>
            {data.weekly.length ? (
              <div
                className="bar-chart"
                role="img"
                aria-label={`Weekly workouts and duration: ${data.weekly.map((x) => `${formatDate(x.week)} ${x.workouts} workouts ${formatDuration(x.seconds)}`).join(", ")}`}
              >
                {data.weekly.map((week) => (
                  <div key={week.week}>
                    <span className={`bar-height-${Math.min(12, Math.max(1, week.workouts))}`}>
                      <b>{week.workouts}</b>
                    </span>
                    <small>
                      {new Date(`${week.week}T12:00:00`).toLocaleDateString(
                        "en-AU",
                        { day: "numeric", month: "short" },
                      )}
                      <br />
                      {formatDuration(week.seconds)}
                    </small>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted">
                Complete workouts to see weekly consistency.
              </p>
            )}
          </Card>
          <Card>
            <h2>Load improvements</h2>
            {data.improvements.length ? (
              <div className="improvement-list">
                {data.improvements.map((item) => (
                  <div key={item.id}>
                    <span>
                      <strong>{item.name}</strong>
                      <small>
                        {item.firstWeight} kg → {item.lastWeight} kg
                      </small>
                    </span>
                    <span
                      className={item.improvement > 0 ? "change-positive" : ""}
                    >
                      {item.improvement > 0 ? "+" : ""}
                      {item.improvement} kg
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <Empty
                title="No load trends yet"
                body="After the same weighted exercise appears in multiple completed workouts, improvement will show here."
              />
            )}
          </Card>
        </>
      )}
    </>
  );
}
